import { observer } from "mobx-react-lite";
import { RefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";

import { SpanCard } from "../../Cards";
import { useStructuredSidebarTraces } from "../../hooks/useStructuredSidebarTraces";
import { SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT } from "../SearchBar/ReviewLogsDrawerControls/constants";

import SpansLoadingShimmer from "./SpansLoadingShimmer";
import { useInfiniteScroll, useKeyboardNavigation } from "./hooks";

import { NoResultsFooter } from ".";

import LoadingSpinner from "@/components/LoadingSpinner";
import { useUserToken } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { SIDEBAR_MAX } from "@/constants";
import { cn } from "@/lib/utils";
import {
  applyTraceEntityHydrationToItems,
  collectTraceEntityHydrationIds,
  hasTraceEntityHydrationIds,
  useTraceEntityHydration,
} from "@/queries/traces";
import type { TraceEntityHydrationStatus, TraceListItem } from "@/types/traces";

const getHydrationStatus = ({
  hasHydratableEntities,
  isError,
  hasData,
}: {
  hasHydratableEntities: boolean;
  isError: boolean;
  hasData: boolean;
}): TraceEntityHydrationStatus => {
  if (!hasHydratableEntities || hasData) {
    return "complete";
  }
  if (isError) {
    return "error";
  }
  return "pending";
};

const Spans = observer(
  ({
    inputRef,
    secondarySidebarOpen = true,
  }: {
    inputRef: RefObject<HTMLInputElement | null>;
    secondarySidebarOpen?: boolean;
  }) => {
    const { traceId } = useParams();
    const userToken = useUserToken();
    const workspaceId = useUser()?.activeWorkspaceId ?? 0;
    const tracesQuery = useStructuredSidebarTraces();

    const traces = useMemo(
      () => tracesQuery.data?.pages?.flatMap(page => page.items) ?? [],
      [tracesQuery.data],
    );
    const hydrationIds = useMemo(() => collectTraceEntityHydrationIds(traces), [traces]);
    const hasHydratableEntities = hasTraceEntityHydrationIds(hydrationIds);
    const traceEntityHydration = useTraceEntityHydration({
      userToken,
      workspaceId,
      items: traces,
      enabled: traces.length > 0,
    });
    const hydrationStatus = getHydrationStatus({
      hasHydratableEntities,
      isError: traceEntityHydration.isError,
      hasData: Boolean(traceEntityHydration.data),
    });
    const displayTraces = useMemo(
      () =>
        applyTraceEntityHydrationToItems({
          items: traces,
          hydration: traceEntityHydration.data,
          status: hydrationStatus,
        }),
      [hydrationStatus, traceEntityHydration.data, traces],
    );

    const sidebarLogContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const handleScrollToTop = () => {
        sidebarLogContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
      };
      window.addEventListener(SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT, handleScrollToTop);
      return () =>
        window.removeEventListener(SCROLL_REVIEW_LOGS_DRAWER_TO_TOP_EVENT, handleScrollToTop);
    }, []);

    const getNextId = useCallback(
      (id?: string) => {
        const index = displayTraces.findIndex(item => item.trace_id === id);
        if (index < displayTraces.length - 1) {
          const nextItem = displayTraces[index + 1];
          return { id: nextItem.trace_id, type: "traces" };
        }
        return null;
      },
      [displayTraces],
    );

    const getPreviousId = useCallback(
      (id?: string) => {
        const index = displayTraces.findIndex(item => item.trace_id === id);
        if (index > 0) {
          const prevItem = displayTraces[index - 1];
          return { id: prevItem.trace_id, type: "traces" };
        }
        return null;
      },
      [displayTraces],
    );

    const scrollToLogBox = useCallback(
      (id: string) => {
        if (sidebarLogContainerRef.current) {
          const container = sidebarLogContainerRef.current;
          const element = container.querySelector(`[data-request-id="${id}"]`) as HTMLElement;

          if (element) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const offsetTop = elementRect.top - containerRect.top;

            let scrollTo = container.scrollTop + offsetTop;

            if (offsetTop <= 0) {
              scrollTo = container.scrollTop + offsetTop - 16;
            }

            const containerHeight = container.clientHeight;
            const elementHeight = element.clientHeight;
            if (offsetTop + elementHeight > containerHeight) {
              scrollTo = container.scrollTop + offsetTop - containerHeight + elementHeight + 16;
            }

            container.scrollTo({
              top: scrollTo,
              behavior: "smooth",
            });
          }
        }
      },
      [sidebarLogContainerRef],
    );

    useKeyboardNavigation({
      inputRef,
      items: displayTraces,
      getNextId,
      getPreviousId,
      scrollToLogBox,
      currentId: traceId ?? "",
      currentType: "traces",
      onGroupPage: false,
      secondarySidebarOpen,
    });

    const {
      fetchNextPage: loadNextPage,
      hasNextPage,
      isFetchingNextPage,
      isFetching,
    } = tracesQuery;

    const fetchNextPage = useCallback(() => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }

      void loadNextPage();
    }, [hasNextPage, isFetchingNextPage, loadNextPage]);

    const isRefetchingFirstPage = isFetching && !isFetchingNextPage && traces.length > 0;

    const infiniteScrollRef = useInfiniteScroll(
      fetchNextPage,
      !!hasNextPage && !isFetchingNextPage && !isRefetchingFirstPage,
      sidebarLogContainerRef,
    );

    const noTracesFound = useMemo(() => traces.length === 0, [traces.length]);
    const isInitialLoading = isFetching && noTracesFound;

    return (
      <nav
        className={cn(
          "flex h-full flex-col space-y-1 overflow-x-hidden px-1 pb-4 pt-2",
          isInitialLoading ? "overflow-y-hidden" : "overflow-y-auto",
        )}
        ref={sidebarLogContainerRef}
      >
        {isInitialLoading && <SpansLoadingShimmer />}
        {!isInitialLoading &&
          displayTraces
            .slice(0, SIDEBAR_MAX)
            .map((item: TraceListItem) => <SpanCard key={item.trace_id} trace={item} />)}
        {hasNextPage && !isInitialLoading && (
          <div ref={infiniteScrollRef} className="flex justify-center py-2">
            {isFetchingNextPage ? <LoadingSpinner size={5} /> : <div className="h-1" />}
          </div>
        )}

        {noTracesFound && !isFetching && <NoResultsFooter />}
      </nav>
    );
  },
);

export default Spans;
